/**
 * Il logo che gira, al posto dello splash fermo.
 *
 * ## Cosa vede chi apre l'app
 *
 * 1. il marchio **fermo**, disegnato dal sistema operativo (i PNG in `res/drawable-<densita>`, e lo
 *    storyboard su iOS): è l'unica cosa che uno splash nativo può essere, perché esiste prima della
 *    WebView e non ha JavaScript;
 * 2. lo stesso marchio, **ora animato**: questo componente, montato a tutto schermo appena React
 *    parte. Lo sfondo e la posizione del logo sono gli stessi del PNG, quindi il passaggio fra i due
 *    non si vede — cambia solo che comincia a muoversi;
 * 3. l'applicazione.
 *
 * Il PNG del punto 1 **è** un fotogramma di questa scena, reso con la stessa camera e le stesse
 * luci: non un disegno somigliante. È il motivo per cui la giunzione regge.
 *
 * ## Le due regole che rendono la cosa sicura
 *
 * - **Sparisce sempre.** Il ciclo del logo la chiude, ma un tetto di tempo la chiude comunque: senza
 *   WebGL il logo non parte e senza questo l'utente resterebbe davanti a un marchio, per sempre.
 *   Il tetto qui dentro è più corto di quello di `splashNativo`, che è l'ultima rete.
 * - **Fuori dall'app non esiste.** Sul web non rende niente: il browser ha già la sua schermata di
 *   caricamento, che è la pagina.
 */
import { useEffect, useRef, useState } from 'react';
import { MaewayLogoLazy } from '@/shared/logo3d/MaewayLogoLazy';
import { isNative } from '@/shared/lib/nativePlatform';
import { hideNativeSplash } from '@/shared/lib/splashNativo';

/** Oltre questo tempo il velo se ne va, animazione finita o no. */
const MAX_DURATION_MS = 3_000;
/** Durata della dissolvenza in uscita. */
const FADE_MS = 320;

/** Lo stesso gradiente della schermata di login, e dello sfondo dei PNG nativi. */
const BACKGROUND = 'linear-gradient(160deg, #FBFBFC 0%, #F2F5F8 100%)';

export function AnimatedSplash() {
  /* Si decide **una volta sola**, al primo render: se `isNative()` cambiasse idea a metà — non
     succede, ma il costo di garantirlo è zero — il velo non deve comparire a sorpresa dopo. */
  const [attivo, setActive] = useState(() => isNative());
  const [exit, setExit] = useState(false);
  const closingRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!attivo) return;

    /* Lo splash **nativo** si chiude subito: sotto c'è già questo velo, identico. Aspettare che
       l'animazione finisca lascerebbe l'immagine di sistema sopra il logo che gira. */
    void hideNativeSplash();

    let exited = false;
    const close = () => {
      if (exited) return;
      exited = true;
      setExit(true);
      setTimeout(() => setActive(false), FADE_MS);
    };
    closingRef.current = close;

    const cap = setTimeout(close, MAX_DURATION_MS);
    return () => clearTimeout(cap);
  }, [attivo]);

  if (!attivo) return null;

  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10_000,
        background: BACKGROUND,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: exit ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease-out`,
        /* A dissolvenza iniziata l'app sotto è già viva: senza questo, il velo invisibile
           continuerebbe a mangiarsi i tocchi per un terzo di secondo. */
        pointerEvents: exit ? 'none' : 'auto',
      }}
    >
      {/* Senza WebGL il fallback è tutto ciò che si vede, e il ciclo non arriverà mai: il velo se
          ne va col tetto di tempo. Il marchio resta comunque quello, perché il PNG nativo che lo
          precede è lo stesso disegno — quindi qui il fallback può essere vuoto. */}
      <div style={{ width: '58vmin', height: '58vmin', maxWidth: 420, maxHeight: 420 }}>
        <MaewayLogoLazy
          clock
          autoPlay
          padding={1.12}
          onCycleEnd={() => closingRef.current?.()}
          fallback={<div style={{ width: '100%', height: '100%' }} />}
        />
      </div>
    </div>
  );
}
